import React, { useEffect, useState, useRef } from 'react';
import { setAutoplay, getVideoMimeType, getVideoType } from '../../../../../Shared/AttraxTs/Generic/VideoStringHandler';

const AUDIO_SYNC_THRESHOLD_SECONDS = 0.3;

interface VideoProps {
    snippetClassName: string
    url?: string
    thumbnail?: string
    thumbnailAlt?: string
    audioDescriptionUrl?: string
    duration?: string
    playBtnText?: string
}

const VideoComponent: React.FC<VideoProps> = ({
    snippetClassName,
    url,
    thumbnail,
    thumbnailAlt,
    audioDescriptionUrl,
    duration,
    playBtnText,
}) => {
    const [isPlaying, setPlaying] = useState(false);
    const videoType = getVideoType(url ?? '');
    const videoRef = useRef<HTMLVideoElement | null>(null);
    const audioDescriptionRef = useRef<HTMLAudioElement | null>(null);
    const hasAudioDescription = !!audioDescriptionUrl;

    // Synchronize audio description with video playback (native video only)
    useEffect(() => {
        const video = videoRef.current;
        const audio = audioDescriptionRef.current;

        if (!video || !audio) return;

        const handlePlay = () => {
            audio.play().catch((error) => {
                console.warn('Audio description playback failed:', error.message);
            });
        };

        const handlePause = () => audio.pause();

        const handleSync = () => {
            if (Math.abs(video.currentTime - audio.currentTime) > AUDIO_SYNC_THRESHOLD_SECONDS) {
                audio.currentTime = video.currentTime;
            }
        };

        video.addEventListener('play', handlePlay);
        video.addEventListener('pause', handlePause);
        video.addEventListener('seeking', handleSync);
        video.addEventListener('timeupdate', handleSync);

        return () => {
            video.removeEventListener('play', handlePlay);
            video.removeEventListener('pause', handlePause);
            video.removeEventListener('seeking', handleSync);
            video.removeEventListener('timeupdate', handleSync);
        };
    }, [isPlaying]);

    const dimensions = { width: 1280, height: 720 };

    const renderPlayer = () => {
        if (!isPlaying || !url) return null;

        const videoClass = `${snippetClassName}__video`;

        if (videoType === 'video') {
            return (
                <>
                    <video
                        className={videoClass}
                        {...dimensions}
                        controls
                        controlsList="nodownload"
                        autoPlay
                        ref={videoRef}
                    >
                        <track kind="captions" />
                        <source src={url} type={getVideoMimeType(url)} />
                        Your browser does not support the video tag.
                    </video>
                    {hasAudioDescription && (
                        /* eslint-disable-next-line jsx-a11y/media-has-caption */
                        <audio
                            ref={audioDescriptionRef}
                            src={audioDescriptionUrl}
                            style={{ display: 'none' }}
                            aria-label={`{{js.snippet.video.audio-description.aria-label}}`}
                        />
                    )}
                </>
            );
        }

        // iframe-based players (YouTube, Vimeo)
        return (
            <iframe
                className={videoClass}
                {...dimensions}
                src={setAutoplay(url)}
                title={`{{js.snippet.video.iframe.title}}`}
                allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
                referrerPolicy={videoType === 'youtube' ? 'strict-origin-when-cross-origin' : undefined}
                allowFullScreen
            />
        );
    };

    const playingClass = isPlaying ? 'snippet-video--playing' : '';

    return (
        <div className={`${snippetClassName}__video-element ${playingClass}`}>
            <div className={`${snippetClassName}__container-video-player`}>
                {!isPlaying && thumbnail && (
                    <figure className={`${snippetClassName}__container-thumbnail`}>
                        <img
                            src={thumbnail}
                            alt={thumbnailAlt || ''}
                            className={`${snippetClassName}__thumbnail`}
                            loading="lazy"
                            {...dimensions}
                        />
                    </figure>
                )}
                {url && (
                    <div className={`${snippetClassName}__container-video`}>
                        {!isPlaying && thumbnail && (
                            <button
                                className={`${snippetClassName}__button-play`}
                                aria-label={`{{js.snippet.video.button.play.aria-label}}`}
                                onClick={() => setPlaying(true)}
                            > 
                            <span className={`${snippetClassName}__button-play-text`}>{playBtnText}</span><span className={`${snippetClassName}__button-play-duration`}>{duration}</span> </button> 
                        )}
                        {renderPlayer()}
                    </div>
                )}
            </div>
        </div>
    );
};

export default VideoComponent;